'use client';

import { ReactNode } from 'react';
import { SearchBar, SearchBarConfig } from './search-bar';
import { FilterSection } from './search-bar/filter-section';
import { CatalogGrid } from './catalog-grid';
import { CatalogProvider, useCatalog } from './catalog-provider';
import { useLayout } from '@/contexts/LayoutContext';
import type {
  CatalogItem,
  FilterCategory,
  CatalogType,
  SelectedFiltersType,
} from '@/lib/services/catalog/catalog-types';

export interface CatalogPageProps {
  items: CatalogItem[];
  searchConfig: SearchBarConfig;
  filterCategories: FilterCategory[];
  initialSortOption?: string;
  initialViewMode?: 'grid4' | 'grid6' | 'grid8' | 'table' | 'gallery';
  catalogType?: CatalogType;
  children?: ReactNode;
  headerContent?: ReactNode;
  // Infinity scroll için yeni prop'lar
  isLoading?: boolean;
  isError?: boolean;
  hasMore?: boolean;
  isFetchingNextPage?: boolean;
  onSearchChange?: (value: string) => void;
  onSortChange?: (option: string) => void;
  onViewModeChange?: (mode: string) => void;
  totalDocs?: number; // Toplam belge sayısı
  // Filtre işlevselliği için yeni prop'lar
  selectedFilters?: SelectedFiltersType;
  onFilterChange?: (filterKey: string, values: string[]) => void;
}

// İç bileşen - Context'ten gelen verileri kullanır
function CatalogPageContent({
  filterCategories,
  searchConfig,
  catalogType = 'all',
  headerContent,
  isLoading,
  isError,
  hasMore,
  isFetchingNextPage,
  onSearchChange,
  onSortChange,
  onViewModeChange,
  totalDocs,
  selectedFilters: _selectedFilters,
  onFilterChange,
}: {
  filterCategories: FilterCategory[];
  searchConfig: SearchBarConfig;
  catalogType: CatalogType;
  headerContent?: ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  hasMore?: boolean;
  isFetchingNextPage?: boolean;
  onSearchChange?: (value: string) => void;
  onSortChange?: (option: string) => void;
  onViewModeChange?: (mode: string) => void;
  totalDocs?: number;
  selectedFilters?: SelectedFiltersType;
  onFilterChange?: (filterKey: string, values: string[]) => void;
}) {
  const { isCartOpen } = useLayout();
  const {
    searchValue: _searchValue,
    isFilterSectionVisible,
    isSortOpen,
    isGridOpen,
    sortOption,
    viewMode,
    filteredItems,
    setSearchValue,
    clearFilters,
    handleFilterChange: contextHandleFilterChange,
    resetCategory,
    toggleFilterSection,
    toggleSortMenu,
    toggleGridMenu,
    handleSortChange,
    handleViewModeChange,
    selectedFilters: contextSelectedFilters,
  } = useCatalog();

  // Dış bileşenden gelen değişiklik işleyicilerini kullan
  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);
    if (onSearchChange) onSearchChange(value);
  };

  const handleSortOptionChange = (option: string) => {
    handleSortChange(option);
    if (onSortChange) onSortChange(option);
  };

  // Sonra handleViewModeChange fonksiyonunda değişiklik yapın:
  const handleViewModeOptionChange = (mode: string) => {
    handleViewModeChange(mode);
    if (typeof window !== 'undefined') {
      localStorage.setItem('viewMode', mode);
    }
    if (onViewModeChange) onViewModeChange(mode);
  };

  const handleFilterChange = (filterKey: string, values: string[]) => {
    if (onFilterChange) {
      onFilterChange(filterKey, values);
    } else {
      // Tek bir değer için contextHandleFilterChange'i çağırmak yerine,
      // her bir değer için ayrı ayrı çağırıyoruz
      const currentValues = contextSelectedFilters[filterKey] || [];

      // Eklenen ve çıkarılan değerleri belirle
      const addedValues = values.filter(v => !currentValues.includes(v));
      const removedValues = currentValues.filter(v => !values.includes(v));

      // Eklenen değerleri işle
      addedValues.forEach(value => {
        contextHandleFilterChange(filterKey, value);
      });

      // Çıkarılan değerleri işle
      removedValues.forEach(value => {
        contextHandleFilterChange(filterKey, value);
      });
    }
  };

  return (
    <div className="h-full overflow-auto relative pb-28 lg:pb-36" id="catalog-scroll-container">
      {/* Header Content (e.g. collection about panel) */}
      {headerContent}
      {/* Artworks Grid with animation */}
      <div className="transition-all duration-500 ease-in-out px-[15px] pt-[15px]">
        <CatalogGrid
          artworks={filteredItems}
          filteredArtworks={filteredItems}
          sortOption={sortOption}
          viewMode={viewMode}
          catalogType={catalogType}
          isLoading={isLoading}
          isError={isError}
          hasMore={hasMore}
          isFetchingNextPage={isFetchingNextPage}
        />
      </div>
      {/* Sticky Search and Filter Bar */}
      {/* <div className="sticky bottom-0 z-10 bg-white/80 backdrop-blur-xs border-b border-zinc-100"> */}
      {/* <div className="sticky bottom-0 z-50 bg-white/80 backdrop-blur-xs border-b border-zinc-100"> */}
      <div
        data-catalog-controls=""
        className="fixed z-50 bg-white border-b border-zinc-100
        rounded-md
        left-[0.5rem] bottom-[0.5rem]
        lg:bottom-[2.5rem]
        "
        style={{
          right: isCartOpen ? 'calc(415px + 0.6rem)' : '0.6rem',
          transition: 'right 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <section className="flex flex-col px-0 pt-0 pb-0 ">
          {/* Filter Section - Conditional rendering */}
          {isFilterSectionVisible && (
            <div className="px-[5px] pb-[5px]">
              <FilterSection
                filterCategories={filterCategories}
                selectedFilters={contextSelectedFilters}
                onFilterChange={handleFilterChange}
                onResetCategory={resetCategory}
              />
            </div>
          )}

          {/* Search Bar */}
          <div className="px-[5px] pt-[5px] mb-[5px]">
            <SearchBar
              onChange={handleSearchValueChange}
              onReset={clearFilters}
              filteredCount={filteredItems.length}
              totalCount={totalDocs} // Burada toplam sayı da gösterilebilir
              onToggleFilters={toggleFilterSection}
              isFiltersOpen={isFilterSectionVisible}
              onSortToggle={toggleSortMenu}
              isSortOpen={isSortOpen}
              sortOption={sortOption}
              onSortChange={handleSortOptionChange}
              onGridToggle={toggleGridMenu}
              isGridOpen={isGridOpen}
              gridOption={viewMode}
              onGridChange={handleViewModeOptionChange}
              config={searchConfig}
              isFetchingNextPage={isFetchingNextPage}
              hasMore={hasMore}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

// Ana bileşen - Provider ile sarmalayarak context sağlar
export function CatalogPage({
  items,
  searchConfig,
  filterCategories,
  initialSortOption = 'newest',
  initialViewMode = 'grid6',
  catalogType = 'all',
  children,
  headerContent,
  isLoading,
  isError,
  hasMore,
  isFetchingNextPage,
  onSearchChange,
  onSortChange,
  onViewModeChange,
  totalDocs,
  selectedFilters,
  onFilterChange,
}: CatalogPageProps) {
  return (
    <CatalogProvider
      items={items}
      initialSortOption={initialSortOption}
      initialViewMode={initialViewMode}
      selectedFilters={selectedFilters}
      onFilterChange={onFilterChange}
    >
      <CatalogPageContent
        filterCategories={filterCategories}
        searchConfig={searchConfig}
        catalogType={catalogType}
        headerContent={headerContent}
        isLoading={isLoading}
        isError={isError}
        hasMore={hasMore}
        isFetchingNextPage={isFetchingNextPage}
        onSearchChange={onSearchChange}
        onSortChange={onSortChange}
        onViewModeChange={onViewModeChange}
        totalDocs={totalDocs}
        selectedFilters={selectedFilters}
        onFilterChange={onFilterChange}
      />
      {children}
    </CatalogProvider>
  );
}
